import { useState, useEffect } from 'react'
import { api } from '../lib/api.js'
import { UrsLogo } from './UrsLogo.jsx'
import { gotoProjekt } from '../lib/moduleLink.js'

const PAGE_TITLES = {
  emails: 'Emails',
  dashboard: 'Pendenzen',
  projects: 'Projekte',
  projekt: 'Projekt',
  chat: 'Urs',
  calendar: 'Kalender',
  ablage: 'Ablage',
  briefe: 'Briefe',
  notizen: 'Notizen',
  timesheet: 'Stundenliste',
  fahrtenbuch: 'Fahrtenbuch',
  kv: 'Baukosten',
  grobks: 'Kostenschätzung',
  preisdatenbank: 'Preisdatenbank',
  mutationen: 'Mutationen',
  kostenuebersicht: 'Kostenübersicht',
  rechnungspruefung: 'Rechnungen',
  zahlungsjournal: 'Zahlungsjournal',
  ausschreibungen: 'Ausschreibungen',
  bauabrechnung: 'Bauabrechnung',
  bautagebuch: 'Bautagebuch',
  regierapporte: 'Regierapporte',
  abnahme: 'Abnahme',
  submission: 'Submission',
  vergabe: 'Vergabe',
  planversand: 'Planversand',
  garantie: 'Garantien',
  behoerden: 'Behörden',
  uebergabe: 'Übergabe',
  werkvertraege: 'Werkverträge',
  versicherungen: 'Versicherungen',
  material: 'Material',
  aufmass: 'Aufmass',
  honorar: 'Honorar',
  fotos: 'Fotos',
  agent: 'Aktionen',
  contacts: 'Kontakte',
  settings: 'Einstellungen',
  gantt: 'Bauablaufplan',
  brain: 'Wissen',
  activity: 'Aktivität',
  vorgaenge: 'Vorgänge',
  'kv-excel': 'KV-Excel',
  'kv-excel-edit': 'KV-Excel',
}

// Sidebar-Gruppen-Definition
// Merkt sich pro Browser, welche Sidebar-Gruppen ein-/ausgeklappt sind.
const SIDEBAR_GROUPS_STORAGE_KEY = 'urs:sidebar-collapsed-groups'

// Projektzentrierte Navigation: die Gruppe 'projekte' listet die aktiven
// Projekte direkt (Klick → '#/projekt/<id>'); projektgebundene Module sind
// standardmässig ausgeblendet und über die Projektseite erreichbar. Wer einzelne
// Module in der Sidebar behalten will, schaltet sie unter Einstellungen → Module
// wieder ein — die Gruppen unten fangen jede mögliche Auswahl auf.
const SIDEBAR_GROUPS = [
  { key: 'uebersicht', label: 'Übersicht', ids: ['dashboard'] },
  { key: 'projekte', label: 'Projekte', ids: ['projects'], projektliste: true },
  { key: 'kommunikation', label: 'Kommunikation', ids: ['emails', 'contacts', 'chat'] },
  { key: 'planung', label: 'Planung', ids: ['calendar', 'timesheet', 'fahrtenbuch', 'gantt'] },
  { key: 'kosten', label: 'Kosten', ids: ['kv', 'kv-excel', 'grobks', 'kostenuebersicht', 'preisdatenbank', 'mutationen', 'ausschreibungen', 'submission', 'rechnungspruefung', 'zahlungsjournal', 'bauabrechnung', 'honorar', 'vergabe', 'werkvertraege'] },
  { key: 'dokumente', label: 'Dokumente', ids: ['ablage', 'briefe', 'notizen', 'fotos', 'planversand'] },
  { key: 'baustelle', label: 'Baustelle', ids: ['bautagebuch', 'regierapporte', 'abnahme', 'uebergabe', 'aufmass', 'garantie', 'material', 'behoerden', 'versicherungen', 'vorgaenge'] },
  { key: 'wissen', label: 'Wissen', ids: ['brain'] },
  { key: 'aktivitaet', label: 'Aktivität', ids: ['activity'] },
]

const ICONS = {
  mail: MailIcon,
  check: CheckIcon,
  folder: FolderIcon,
  bot: BotIcon,
  calendar: CalendarIcon,
  inbox: AblageIcon,
  clock: ClockIcon,
  'file-text': KVIcon,
  'bar-chart': MutationIcon,
  users: ContactsIcon,
  brain: BrainIcon,
  receipt: ReceiptIcon,
  scale: ScaleIcon,
  calculator: CalculatorIcon,
  clipboard: ClipboardIcon,
  edit: EditIcon,
  'check-circle': CheckCircleIcon,
  send: SendIcon,
  award: AwardIcon,
  map: MapIcon,
  shield: ShieldIcon,
  landmark: LandmarkIcon,
  key: KeyIcon,
  'file-signature': FileSignatureIcon,
  umbrella: UmbrellaIcon,
  package: PackageIcon,
  ruler: RulerIcon,
  percent: PercentIcon,
  camera: CameraIcon,
  database: DatabaseIcon,
  activity: ActivityIcon,
  'git-merge': GitMergeIcon,
}

export default function Sidebar({ activePage, activeProjectId, onNavigate, user, stats, onLogout }) {
  const [modules, setModules] = useState(null)
  const [projekte, setProjekte] = useState([])
  const [logoUrl, setLogoUrl] = useState(null)
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      const saved = localStorage.getItem(SIDEBAR_GROUPS_STORAGE_KEY)
      return saved ? JSON.parse(saved) : {}
    } catch { return {} }
  })
  const [profileOpen, setProfileOpen] = useState(false)

  useEffect(() => {
    api.sidebarModules()
      .then(mods => { setModules(mods) })
      .catch(() => {
        setModules(null)
      })
    // Aktive Projekte für die Sidebar-Gruppe 'Projekte' (projektzentrierte Navigation)
    api.projectsSidebar()
      .then(alle => setProjekte(alle || []))
      .catch(() => setProjekte([]))
    // Logo laden — sidebar_logo='urs' erzwingt das Urs-Logo trotz Firmenlogo
    api.brandingGet()
      .then(b => { if (b.logo_path && b.sidebar_logo !== 'urs') setLogoUrl(api.brandingLogoUrl()) })
      .catch(() => {})
    // Gruppen-Klappzustand vom Konto laden (geräteübergreifend).
    // localStorage hat die Sidebar bereits ohne Flicker initialisiert; der
    // Server-Wert überschreibt nur, falls vorhanden, und aktualisiert den Cache.
    api.getUserSettings()
      .then(s => {
        if (!s?.sidebar_collapsed_groups) return
        const parsed = JSON.parse(s.sidebar_collapsed_groups)
        if (parsed && typeof parsed === 'object') {
          setCollapsedGroups(parsed)
          try { localStorage.setItem(SIDEBAR_GROUPS_STORAGE_KEY, JSON.stringify(parsed)) } catch { /* ignore */ }
        }
      })
      .catch(() => { /* offline/kein Wert — localStorage greift */ })
  }, [])

  function toggleGroup(key) {
    setCollapsedGroups(prev => {
      const next = { ...prev, [key]: !prev[key] }
      try { localStorage.setItem(SIDEBAR_GROUPS_STORAGE_KEY, JSON.stringify(next)) } catch { /* localStorage nicht verfügbar */ }
      // Zusätzlich ans Konto hängen (geräteübergreifend), fire-and-forget
      api.saveUserSettings({ sidebar_collapsed_groups: JSON.stringify(next) }).catch(() => { /* localStorage reicht */ })
      return next
    })
  }

  function openSearchSpotlight() {
    // Trigger the same Cmd+/ shortcut that SearchSpotlight listens to
    window.dispatchEvent(new KeyboardEvent('keydown', { key: '/', metaKey: true, bubbles: true }))
  }

  // Badge-Mapping
  const badges = {
    emails: stats?.unread,
    dashboard: stats?.pendingActions,
    ablage: stats?.ablageUnread,
  }

  // Build a lookup of visible module links
  const visibleLinks = {}
  if (modules) {
    modules.filter(m => m.visible).forEach(m => {
      visibleLinks[m.module_id] = {
        id: m.module_id,
        label: m.label,
        icon: ICONS[m.icon] ? ICONS[m.icon]() : null,
        badge: badges[m.module_id]
      }
    })
  } else {
    // Fallback wenn API noch nicht geladen
    const fallback = [
      { id: 'emails', label: 'Posteingang', icon: <MailIcon />, badge: stats?.unread },
      { id: 'dashboard', label: 'Pendenzen', icon: <CheckIcon />, badge: stats?.pendingActions },
      { id: 'projects', label: 'Projekte', icon: <FolderIcon /> },
      { id: 'chat', label: 'Urs', icon: <BotIcon /> },
      { id: 'calendar', label: 'Kalender', icon: <CalendarIcon /> },
      { id: 'ablage', label: 'Ablage', icon: <AblageIcon />, badge: stats?.ablageUnread },
      { id: 'timesheet', label: 'Stundenliste', icon: <ClockIcon /> },
      { id: 'kv', label: 'Baukosten', icon: <KVIcon /> },
      { id: 'kv-excel', label: 'KV-Excel', icon: <KVIcon /> },
      { id: 'mutationen', label: 'Mutationen', icon: <MutationIcon /> },
      { id: 'kostenuebersicht', label: 'Kostenübersicht', icon: <KVIcon /> },
    ]
    fallback.forEach(l => { visibleLinks[l.id] = l })
  }

  return (
    <nav className="sidebar">
      <div className="sidebar-page-title">
        <h2>{PAGE_TITLES[activePage] || 'Pendenzen'}</h2>
      </div>

      {/* Search trigger */}
      <div className="sidebar-search" onClick={openSearchSpotlight}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16, flexShrink: 0, opacity: 0.5 }}>
          <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <span>Suchen...</span>
        <kbd className="sidebar-search-kbd">{'\u2318'}/</kbd>
      </div>

      <div className="sidebar-nav">
        {SIDEBAR_GROUPS.map(group => {
          const groupLinks = group.ids.filter(id => visibleLinks[id]).map(id => visibleLinks[id])
          if (groupLinks.length === 0 && !(group.projektliste && projekte.length > 0)) return null
          const isCollapsed = collapsedGroups[group.key]
          return (
            <div key={group.key} className="sidebar-group">
              <button
                className="sidebar-group-header"
                onClick={() => toggleGroup(group.key)}
              >
                <span>{group.label}</span>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={`sidebar-group-chevron ${isCollapsed ? 'collapsed' : ''}`}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {/* Projektliste: aktive Projekte direkt anwählbar (eigene Seite pro Projekt) */}
              {!isCollapsed && group.projektliste && projekte.map(p => (
                <button
                  key={`projekt-${p.id}`}
                  className={`sidebar-link ${activePage === 'projekt' && activeProjectId === p.id ? 'active' : ''}`}
                  onClick={() => gotoProjekt(p.id)}
                  title={`${p.project_number ? p.project_number + ' — ' : ''}${p.name}`}
                >
                  <span style={{
                    width: 8, height: 8, borderRadius: 99, flexShrink: 0,
                    background: p.color || 'var(--color-primary)', display: 'inline-block',
                  }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.project_number ? `${p.project_number} ${p.name}` : p.name}
                  </span>
                </button>
              ))}
              {!isCollapsed && groupLinks.map(link => (
                <button
                  key={link.id}
                  className={`sidebar-link ${activePage === link.id ? 'active' : ''}`}
                  onClick={() => onNavigate(link.id)}
                >
                  {link.icon}
                  {group.projektliste && link.id === 'projects' ? 'Alle Projekte' : link.label}
                  {link.badge > 0 && <span className="sidebar-badge">{link.badge}</span>}
                </button>
              ))}
            </div>
          )
        })}

        <div style={{ flex: 1 }} />

      </div>

      <div className="sidebar-footer">
        <div style={{ paddingBottom: 14, marginBottom: 24, borderBottom: '1px solid var(--color-border-light)' }}>
          <button
            className="sidebar-footer-profile"
            onClick={() => setProfileOpen(o => !o)}
            aria-expanded={profileOpen}
            style={{ justifyContent: 'space-between', width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <div className="sidebar-avatar">
                {(user?.displayName || user?.email || '??').split(/[\s@]/).filter(Boolean).slice(0, 2).map(s => s[0].toUpperCase()).join('')}
              </div>
              <div className="sidebar-footer-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user?.displayName || user?.office || 'Benutzer'}
              </div>
            </div>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`sidebar-group-chevron ${profileOpen ? '' : 'collapsed'}`}
              style={{ color: 'var(--color-text-muted)' }}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          {profileOpen && (
            <div style={{ marginTop: 4 }}>
              <button
                className={`sidebar-link ${activePage === 'settings' ? 'active' : ''}`}
                onClick={() => onNavigate('settings')}
              >
                <SettingsIcon />
                Einstellungen
              </button>
              <button className="sidebar-link" onClick={onLogout}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                Abmelden
              </button>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
          {logoUrl
            ? <img src={logoUrl} alt="Logo" style={{ maxHeight: 52, maxWidth: '80%', objectFit: 'contain' }}
                onError={() => setLogoUrl(null)} />
            : <UrsLogo width={38} style={{ color: 'var(--color-text)', opacity: 0.9 }} />}
        </div>
      </div>
    </nav>
  )
}

function MailIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  )
}

function FolderIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    </svg>
  )
}

function BotIcon() {
  // urs-Bot (neues Logo): Kontur-Kopf, schwebende Cursor-Antenne, zwei Augen
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="11.1" y="3" width="1.8" height="5.6" rx="0.9" fill="currentColor" stroke="none" />
      <rect x="3" y="12" width="18" height="9.5" rx="3" />
      <circle cx="8.6" cy="16.8" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="15.4" cy="16.8" r="1.4" fill="currentColor" stroke="none" />
    </svg>
  )
}

function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  )
}

function AblageIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  )
}

function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  )
}

function KVIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="16" y2="17" />
    </svg>
  )
}

function MutationIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8z" />
      <polyline points="14 3 14 8 21 8" />
      <path d="M8 13h2v4H8z" />
      <path d="M14 11h2v6h-2z" />
    </svg>
  )
}

function ContactsIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  )
}

function BrainIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a7 7 0 0 1 7 7c0 2.38-1.19 4.47-3 5.74V17a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 0 1 7-7z" />
      <line x1="9" y1="21" x2="15" y2="21" />
    </svg>
  )
}

function FileSignatureIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <path d="M12 18c1.5 0 2.5-1 3.5-2s1.5-1 2.5-1" />
    </svg>
  )
}

function UmbrellaIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 12a11.05 11.05 0 0 0-22 0zm-5 7a3 3 0 0 1-6 0v-7" />
    </svg>
  )
}

function PackageIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" />
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  )
}

function RulerIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21.73 18l-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3z" />
      <line x1="12" y1="9" x2="12" y2="13" />
    </svg>
  )
}

function PercentIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="5" x2="5" y2="19" />
      <circle cx="6.5" cy="6.5" r="2.5" />
      <circle cx="17.5" cy="17.5" r="2.5" />
    </svg>
  )
}

function DatabaseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
    </svg>
  )
}

function CameraIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  )
}

function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  )
}

function LandmarkIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="22" x2="21" y2="22" />
      <line x1="6" y1="18" x2="6" y2="11" />
      <line x1="10" y1="18" x2="10" y2="11" />
      <line x1="14" y1="18" x2="14" y2="11" />
      <line x1="18" y1="18" x2="18" y2="11" />
      <polygon points="12 2 20 8 4 8" />
      <line x1="2" y1="18" x2="22" y2="18" />
    </svg>
  )
}

function KeyIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.78 7.78 5.5 5.5 0 0 1 7.78-7.78zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
    </svg>
  )
}

function SendIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  )
}

function AwardIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="7" />
      <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
    </svg>
  )
}

function MapIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
      <line x1="8" y1="2" x2="8" y2="18" />
      <line x1="16" y1="6" x2="16" y2="22" />
    </svg>
  )
}

function ClipboardIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
      <line x1="9" y1="10" x2="15" y2="10" />
      <line x1="9" y1="14" x2="15" y2="14" />
      <line x1="9" y1="18" x2="12" y2="18" />
    </svg>
  )
}

function EditIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  )
}

function CheckCircleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  )
}

function ReceiptIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 2v20l3-2 3 2 3-2 3 2 3-2 3 2V2l-3 2-3-2-3 2-3-2-3 2-3-2z" />
      <line x1="8" y1="8" x2="16" y2="8" />
      <line x1="8" y1="12" x2="16" y2="12" />
      <line x1="8" y1="16" x2="12" y2="16" />
    </svg>
  )
}

function ScaleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v18" />
      <path d="M5 7l7-4 7 4" />
      <path d="M2 14l3-7 3 7a5 5 0 0 1-6 0z" />
      <path d="M16 14l3-7 3 7a5 5 0 0 1-6 0z" />
    </svg>
  )
}

function CalculatorIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <line x1="8" y1="6" x2="16" y2="6" />
      <line x1="8" y1="10" x2="10" y2="10" />
      <line x1="14" y1="10" x2="16" y2="10" />
      <line x1="8" y1="14" x2="10" y2="14" />
      <line x1="14" y1="14" x2="16" y2="14" />
      <line x1="8" y1="18" x2="10" y2="18" />
      <line x1="14" y1="18" x2="16" y2="18" />
    </svg>
  )
}

function SettingsIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  )
}

function AdminIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  )
}

function ActivityIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  )
}

function GitMergeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="18" cy="18" r="3" /><circle cx="6" cy="6" r="3" /><path d="M6 21V9a9 9 0 009 9" />
    </svg>
  )
}
